import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';

// Icons should always be lazy loaded, meaning a function that returns a Promise with the SVG markup.
const GRAPH_ICONS_LIGHT = {
    '404': async () => await import('./404.js'),
    'access-denied': async () => await import('./access-denied.js'),
    'account-suspended': async () => await import('./account-suspended.js'),
    'app-construction': async () => await import('./app-construction.js'),
    'browser-not-supported': async () => await import('./browser-not-supported.js'),
    'email-sent': async () => await import('./email-sent.js'),
    'empty-live-channels-list': async () => await import('./empty-live-channels-list.js'),
    'empty-ott-list-page': async () => await import('./empty-ott-list-page.js'),
    'empty-series': async () => await import('./empty-series.js'),
    'empty-playlist': async () => await import('./empty-playlist.js'),
    empty: async () => await import('./empty.js'),
    'link-expired': async () => await import('./link-expired.js'),
    'no-data-retrieved': async () => await import('./no-data-retrieved.js'),
    'no-search-result': async () => await import('./no-search-result.js'),
    'unsubscribe-error': async () => await import('./unsubscribe-error.js'),
    'unsubscribe-success': async () => await import('./unsubscribe-success.js'),
};
// Icons should always be lazy loaded, meaning a function that returns a Promise with the SVG markup.
const GRAPH_ICONS_DARK = {
    '404': async () => await import('./4042.js'),
    'access-denied': async () => await import('./access-denied2.js'),
    'account-suspended': async () => await import('./account-suspended2.js'),
    'app-construction': async () => await import('./app-construction2.js'),
    'browser-not-supported': async () => await import('./browser-not-supported2.js'),
    'email-sent': async () => await import('./email-sent2.js'),
    'empty-live-channels-list': async () => await import('./empty-live-channels-list2.js'),
    'empty-ott-list-page': async () => await import('./empty-ott-list-page2.js'),
    'empty-series': async () => await import('./empty-series2.js'),
    'empty-playlist': async () => await import('./empty-playlist2.js'),
    empty: async () => await import('./empty2.js'),
    'link-expired': async () => await import('./link-expired2.js'),
    'no-data-retrieved': async () => await import('./no-data-retrieved2.js'),
    'no-search-result': async () => await import('./no-search-result2.js'),
    'unsubscribe-error': async () => await import('./unsubscribe-error2.js'),
    'unsubscribe-success': async () => await import('./unsubscribe-success2.js'),
};

const GRAPH_ICON_NAMES = [
    '404',
    'access-denied',
    'account-suspended',
    'app-construction',
    'browser-not-supported',
    'email-sent',
    'empty-live-channels-list',
    'empty-ott-list-page',
    'empty-series',
    'empty-playlist',
    'empty',
    'link-expired',
    'no-data-retrieved',
    'no-search-result',
    'unsubscribe-error',
    'unsubscribe-success',
];

const graphIconCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:-webkit-fit-content;width:fit-content}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}div{display:none}div[data-theme=light]{display:var(--wui-graph-icon--display-light)}div[data-theme=dark]{display:var(--wui-graph-icon--display-dark)}";
const WuiGraphIconStyle0 = graphIconCss;

const checkGraphIconValid = (name) => {
    return name === 'NULL' || typeof name === 'undefined' || !GRAPH_ICON_NAMES.includes(name);
};
const getGraphIconSvgLight = async (name) => {
    if (checkGraphIconValid(name))
        return '';
    const fn = GRAPH_ICONS_LIGHT[name];
    const ret = await fn();
    return ret.default;
};
const getGraphIconSvgDark = async (name) => {
    if (checkGraphIconValid(name))
        return '';
    const fn = GRAPH_ICONS_DARK[name];
    const ret = await fn();
    return ret.default;
};
const WuiGraphIcon = /*@__PURE__*/ proxyCustomElement(class WuiGraphIcon extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.name = 'NULL';
        this.svgLight = '';
        this.svgDark = '';
    }
    // We lazy load all icon SVGs by watching when the `name` and `theme` property change.
    // We then request the icon asynchronously, and once its loaded, we replace
    // the internal state with the new SVG markup.
    async watchName() {
        this.svgLight = await getGraphIconSvgLight(this.name);
        this.svgDark = await getGraphIconSvgDark(this.name);
    }
    componentWillLoad() {
        // Load the initial icon SVG by triggering the watch function.
        this.watchName();
    }
    render() {
        return (h(Host, { key: 'b760fb23894cbba97a5ffcbf5c402f43c4f42772' }, h("div", { key: 'd3b7c5f77727a8653c8d9ad4928754342b9d4413', innerHTML: this.svgLight, "data-theme": "light" }), h("div", { key: '4ec87bf441ffd51f2de5eaee4cca5feb38304e63', innerHTML: this.svgDark, "data-theme": "dark" })));
    }
    static get watchers() { return {
        "name": ["watchName"]
    }; }
    static get style() { return WuiGraphIconStyle0; }
}, [1, "wui-graph-icon", {
        "name": [1],
        "svgLight": [32],
        "svgDark": [32]
    }, undefined, {
        "name": ["watchName"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-graph-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-graph-icon":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiGraphIcon);
            }
            break;
    } });
}
defineCustomElement();

export { WuiGraphIcon as W, defineCustomElement as d };

//# sourceMappingURL=graph-icon.js.map